/**
 * Sidebar and ⌘K destinations, in the order of the approved prototype.
 * Gating is presentation only — the API remains the authority on access.
 */
import {
  Bell,
  ChartColumn,
  Code,
  CreditCard,
  LayoutDashboard,
  Plug,
  Scale,
  Scaling,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Tag,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { UserRole } from '@/api/types';
import { SCREENS, type ScreenId } from './screens';

export type NavGate = 'accountAdmin' | 'predictionOperator';

export type NavItem = { screen: ScreenId; icon: LucideIcon; gate?: NavGate };
export type NavGroup = { label: string | null; items: NavItem[] };

export const NAV: NavGroup[] = [
  {
    label: null,
    items: [
      { screen: 'overview', icon: LayoutDashboard },
      { screen: 'performance', icon: ChartColumn },
      { screen: 'attributeInsights', icon: ScanSearch },
      { screen: 'competitorInsights', icon: Users },
      { screen: 'creativeLab', icon: Sparkles },
    ],
  },
  {
    label: 'Tools',
    items: [
      { screen: 'attributes', icon: Tag },
      { screen: 'alerts', icon: Bell },
      { screen: 'aiResizing', icon: Scaling },
      { screen: 'revenueForecasts', icon: TrendingUp },
      { screen: 'forecastAdmin', icon: ShieldCheck, gate: 'predictionOperator' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { screen: 'connections', icon: Plug },
      { screen: 'metricWeights', icon: Scale },
      { screen: 'apiAccess', icon: Code, gate: 'accountAdmin' },
      { screen: 'users', icon: Users },
      { screen: 'billing', icon: CreditCard },
    ],
  },
];

export type Access = { role: UserRole | undefined; isPredictionOperator: boolean };

export const canSee = (item: NavItem, access: Access): boolean => {
  if (item.gate === 'accountAdmin') return access.role === 'admin' || access.role === 'owner';
  if (item.gate === 'predictionOperator') return access.isPredictionOperator;
  return true;
};

export const visibleNav = (access: Access): NavGroup[] =>
  NAV.map((group) => ({ ...group, items: group.items.filter((item) => canSee(item, access)) })).filter(
    (group) => group.items.length > 0,
  );

export const screenOf = (item: NavItem) => SCREENS[item.screen];

/** Active when the current path is the screen or below it (/attribute-analysis/explore → Attribute insights). */
export const isActivePath = (pathname: string, screenPath: string): boolean =>
  pathname === screenPath || pathname.startsWith(`${screenPath}/`);
